import { useNavigate, useLocation } from 'react-router-dom'
import { useProfile } from '../../context/ProfileContext'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { useSidebar } from '../../context/SidebarContext'
import { useSidebarBadges } from '../../context/SidebarBadgeContext'
import { CompanySwitcher } from '../CompanySwitcher'
import {
  House,
  SquaresFour,
  Package,
  Boat,
  FileText,
  Money,
  CheckCircle,
  ChartBar,
  Gear,
  Buildings,
  UserCheck,
  Users,
  UsersThree,
  CreditCard,
  SignOut,
  CaretLeft,
  CaretRight,
  EnvelopeSimple,
  ShieldCheck,
  Warning,
  TrendDown,
} from '@phosphor-icons/react'

interface MenuItem {
  icon: any
  label: string
  path: string
  badge?: number
}

export function Sidebar() {
  const { collapsed, toggleCollapsed } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentProfile } = useProfile()
  const { logout, user } = useAuth()
  const { t } = useLanguage()
  const { badges } = useSidebarBadges()

  // DevTopBar aparece apenas para masters em dev mode
  const hasDevBar = !import.meta.env.PROD && user?.user_master

  // Menus específicos por perfil
  const menusByProfile: Record<string, MenuItem[]> = {
    master: [
      { icon: House, label: 'Dashboard', path: '/master/dashboard' },
      { icon: Package, label: 'Torre de Controle', path: '/master/control-tower' },
      { icon: Buildings, label: 'Tenants', path: '/master/tenants' },
      { icon: UserCheck, label: 'Embarcadores Pendentes', path: '/master/pending-shippers' },
      { icon: Users, label: 'Usuários', path: '/master/users' },
      { icon: ShieldCheck, label: 'Agentes Plataforma', path: '/master/platform-agents' },
      { icon: CreditCard, label: 'Billing', path: '/master/billing' },
      { icon: Warning, label: 'Erros', path: '/master/errors' },
      { icon: EnvelopeSimple, label: 'Emails', path: '/master/emails' },
      { icon: ChartBar, label: 'Analytics', path: '/master/analytics' },
      { icon: Gear, label: 'Configurações', path: '/master/settings' },
    ],
    shipper: [
      { icon: House, label: t.dashboard.title || 'Dashboard', path: '/shipper/dashboard' },
      { icon: SquaresFour, label: 'Torre de Controle', path: '/shipper/control-tower' },
      { icon: Package, label: 'Cotações', path: '/shipper/quotes' },
      { icon: CheckCircle, label: 'Fretes Contratados', path: '/shipper/shipments?status=pending' },
      { icon: Boat, label: 'Embarques', path: '/shipper/shipments' },
      { icon: FileText, label: 'Documentos', path: '/shipper/documents' },
      { icon: Money, label: 'Propostas', path: '/shipper/offers' },
      { icon: UsersThree, label: 'Meus Agentes', path: '/shipper/agents' },
      { icon: ChartBar, label: 'Relatórios', path: '/shipper/reports' },
      { icon: Gear, label: 'Configurações', path: '/shipper/settings' },
    ],
    'freight-forwarder': [
      { icon: House, label: t.dashboard.title || 'Dashboard', path: '/freight-forwarder/dashboard' },
      { icon: Package, label: 'Oportunidades', path: '/freight-forwarder/opportunities' },
      { icon: Money, label: 'Minhas Propostas', path: '/freight-forwarder/my-offers' },
      { icon: CheckCircle, label: 'Propostas Aprovadas', path: '/freight-forwarder/my-offers?status=accepted' },
      { icon: TrendDown, label: 'Propostas Perdidas', path: '/freight-forwarder/lost-offers' },
      { icon: EnvelopeSimple, label: 'Convites', path: '/freight-forwarder/invites', badge: badges.pendingInvites || undefined },
      { icon: Boat, label: 'Embarques Ativos', path: '/freight-forwarder/shipments' },
      { icon: ChartBar, label: 'Performance', path: '/freight-forwarder/performance' },
      { icon: Gear, label: 'Configurações', path: '/freight-forwarder/settings' },
    ],
  }

  const currentMenu = menusByProfile[currentProfile] || menusByProfile.shipper

  const isActive = (path: string) => {
    const [p, q] = path.split('?')
    const pathMatch = location.pathname === p || location.pathname.startsWith(p + '/')
    if (!pathMatch) return false
    // Items that share a path but differ by query (e.g. Minhas Propostas vs
    // Propostas Aprovadas on /my-offers): the query item matches only when the
    // query is present; the plain item only when no such status filter is set.
    if (q) return location.search.includes(q)
    return !location.search.includes('status=')
  }

  return (
    <div
      className={`fixed left-0 flex flex-col transition-all duration-300 z-50 ${collapsed ? 'w-20' : 'w-64'} ${hasDevBar ? 'top-10 h-[calc(100vh-40px)]' : 'top-0 h-screen'}`}
      style={{ backgroundColor: 'oklch(0.208 0.042 265.755)' }}
    >
      {/* Logo / Brand */}
      <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10 relative">
        {!collapsed && (
          <>
            <img src="/logo2.png" alt="Eximtech Logo" className="w-8 h-8" />
            <h1 className="text-xl font-bold text-white">
              <span className="text-primary">Exim</span>tech
            </h1>
          </>
        )}
        {collapsed && <img src="/logo2.png" alt="Eximtech Logo" className="w-8 h-8 mx-auto" />}

        {/* Toggle Button */}
        <button
          onClick={toggleCollapsed}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors shadow-lg"
        >
          {collapsed ? <CaretRight size={16} weight="bold" /> : <CaretLeft size={16} weight="bold" />}
        </button>
      </div>


      {/* Company Switcher - Show if user has companies */}
      {!collapsed && user?.companies && user.companies.length > 0 && (
        <div className="px-4 py-3 border-b border-white/10">
          <CompanySwitcher />
        </div>
      )}

      {/* Menu Items */}
      <nav className="flex-1 overflow-y-auto scrollbar-hide py-4">
        <div className="space-y-1 px-3">
          {currentMenu.map((item) => {
            const active = isActive(item.path)
            const Icon = item.icon

            return (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => navigate(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${collapsed ? 'justify-center' : ''}
                    ${
                      active
                        ? 'text-gray-900'
                        : 'text-white/80 hover:bg-white/5'
                    }
                  `}
                  style={active ? { backgroundColor: 'oklch(0.8112 0.1915 154.509)' } : {}}
                >
                  <Icon size={20} weight={active ? 'fill' : 'regular'} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.badge && (
                        <span
                          className={`
                          px-2 py-0.5 text-xs rounded-full font-semibold
                          ${active ? 'bg-gray-900 text-white' : 'bg-primary text-white'}
                        `}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
                  )}
                </button>
                {/* Tooltip when collapsed */}
                {collapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg">
                    {item.label}
                    {item.badge && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {/* Arrow */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-white/10">
        <div className="relative group">
          <button
            onClick={() => logout()}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5 transition-colors ${collapsed ? 'justify-center' : ''}`}
          >
            <SignOut size={20} />
            {!collapsed && <span>Sair</span>}
          </button>
          {/* Tooltip when collapsed */}
          {collapsed && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg">
              Sair
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          )}
        </div>
        {/* Build Version */}
        {!collapsed && (
          <div className="mt-2 px-3 text-[10px] text-white/40 font-mono">
            v{__BUILD_VERSION__}
          </div>
        )}
      </div>
    </div>
  )
}
